import { Keyboard } from "react-native";
import { clientController } from "../../../cdm/controllers/client-controller";
import { computed, observable, toJS } from "mobx";
import { stateController } from "../../../cdm/controllers/state-controller";
import { UIText } from "../../../config/lang-config";
import { isEmpty, getCookie } from "../../../utils/helpers";
import { apiService } from "../../../cdm/services/api-service";
import { endpointConfig, serverConfig } from "../../../config/api-config";
import { userStatus, env, rootDomain } from "../../../config/variable-config";

export class LoginController {
  @observable buttonDisabled = true;
  @observable signingIn = false;

  // MFA state
  @observable mfaRequired = false;
  @observable mfaCode = "";
  @observable mfaTrust = true;
  @observable mfaVerifying = false;
  @observable mfaError = "";
  @observable mfaSessionId = "";
  @observable mfaAttemptsRemaining = 3;

  @computed get autoUsername() {
    const form = stateController.base64form;
    return form && (form.username || form.email || form.phone);
  }
  @computed get autoPassword() {
    const form = stateController.base64form;
    return form && form.password;
  }
  @computed get autoSignIn() {
    return stateController.autoSignIn;
  }
  @computed get invitationData() {
    return stateController.viewInvitationRegState.data;
  }

  @observable fieldData = {
    username: toJS(this.autoUsername) || "",
    password: toJS(this.autoPassword) || ""
  };

  constructor(props) {
    this.props = props;
  }

  componentDidMount() {
    stateController.currentScreen = "Login";
    this.checkAutoSignIn();
    setTimeout(this.validateFields, 100);
  }

  _loginLoading = () => {
    this.signingIn = true;
    return stateController.showPopup({
      title: UIText.loginProgress,
      content: UIText.pleaseWait
    });
  };

  _showError = err => {
    // console.warn(err);
    this.signingIn = false;
    stateController.showPopup({
      title: UIText.loginFailure,
      content:
        (err.response && JSON.stringify(err.response.data).replace(/"/g, "")) ||
        err.message,
      leftButtonText: UIText.generalConfirm,
      dismissOnBackPress: true
    });
  };

  checkAutoSignIn = () => {
    if (this.autoSignIn) {
      this.execSignIn();
      return (stateController.autoSignIn = false);
    }
  };

  updateFields = (field, event) => {
    if (!event) return;
    if (event.nativeEvent && typeof event.nativeEvent.text === "string")
      this.fieldData[field] = event.nativeEvent.text;
    this.validateFields();
  };

  validateFields = () => {
    this.buttonDisabled = !this.fieldData.username || !this.fieldData.password;
  };

  handleLinkPress = (link, event) => {
    if (link === "visitor") {
      this.props.navigation.navigate("Visitor");
    }
    // if (link === "register") {
    //   this.props.navigation.navigate("Registration");
    // }
    if (link === "reset") {
      stateController.viewResetState.resetRequest = true;
      this.props.navigation.navigate("Reset");
    }
  };

  execSignIn = event => {
    if (this.buttonDisabled && !this.autoSignIn) return;

    this.validateFields();
    if (this.buttonDisabled) return;

    Keyboard.dismiss();

    const invitationData = toJS(this.invitationData);

    // Check if this is an invitation login
    if (!isEmpty(invitationData)) {
      // Invitation login - use old flow
      this._loginLoading()
        .then(() => this.fieldData)
        .then(clientController.execSignIn)
        .then(() => this.invitationLogin(invitationData))
        .catch(this.handleLoginError);
      return;
    }

    // Normal login - use new session-based MFA flow
    this.signingIn = true;
    this.buttonDisabled = true;

    // Get trust cookie value
    const trustCookieValue = this.getTrustCookie();

    // Call new session-based MFA check endpoint
    return stateController
      .showPopup({
        title: UIText.postLoginProgress,
        content: UIText.pleaseWait
      })
      .then(() =>
        apiService.async("POST", {
          endpoint: "/auth/mfa/signin-with-mfa-check",
          headers: serverConfig.defaultHeaders,
          data: {
            username: this.fieldData.username.toLowerCase(),
            password: this.fieldData.password,
            trustCookieValue,
            environment: env
          }
        })
      )
      .then(response => {
        const data = response.data || {};

        if (data.requireMfa) {
          // MFA required - show MFA fields
          this.mfaRequired = true;
          this.mfaSessionId = data.sessionId;
          this.mfaCode = "";
          this.mfaError = "";
          this.mfaAttemptsRemaining = 3;
          this.signingIn = false;
          this.buttonDisabled = false;
          return stateController.dismissPopup();
        }

        // No MFA required - complete sign-in with OAuth data
        const oauth = data.oauth;
        return clientController
          .updateOAuth2Data(oauth)
          .then(() =>
            stateController.showPopup({
              title: UIText.postLoginProgress,
              content: UIText.pleaseWait
            })
          )
          .then(clientController.initializeClient)
          .then(stateController.dismissPopup);
      })
      .catch(this.handleLoginError);
  };

  invitationLogin = async invitationData => {
    const oauth = clientController.client.oauth;
    const data = {
      authToken: oauth["access_token"],
      invitationId: invitationData.id,
      invitationHash: invitationData["invitation_hash"]
    };

    return stateController
      .showPopup({
        title: UIText.invitationVerification,
        content: UIText.pleaseWait
      })
      .then(() => {
        apiService.resetOAuthData();
        return apiService.async("POST", {
          endpoint: endpointConfig.invitation_login,
          data
        });
      })
      .then(console.log)
      .then(stateController.dismissPopup)
      .then(() =>
        stateController.showPopup({
          title: UIText.invitationVerification,
          content: UIText.invitationLoginSent,
          leftButtonText: UIText.generalConfirm,
          leftButtonPress: this.execSignIn
        })
      );
  };

  handleLoginError = err => {
    console.error(err);
    this.buttonDisabled = false;
    this.signingIn = false;

    return stateController.dismissPopup().then(() => {
      if (!err.response && !err.message) {
        return this._showError({ message: UIText.serviceUnavailable });
      }
      if (err.response && err.response.status === 400) {
        return this._showError({ message: UIText.loginWrongCred });
      }
      if (err.response && err.response.status === 403) {
        const user = err.response.data;
        if (Number(user.status) === userStatus.unverified) {
          return stateController.showPopup({
            title: UIText.loginFailure,
            content: UIText.loginUnverified,
            buttonSet: [
              {
                title: UIText.loginResendVerification,
                onPress: this.resendVerification
              },
              {
                title: UIText.generalConfirm,
                onPress: () =>
                  stateController
                    .dismissPopup()
                    .then(clientController.execLogout)
              }
            ].map(b => (b.align = "center") && b)
          });
        }
        if (!user.termsAgreed || user.needPasswordReset) {
          stateController.viewResetState.terms = !user.termsAgreed;
          stateController.viewResetState.reset = user.needPasswordReset;
          return this.props.navigation.navigate("Reset");
        }
      }
      return this._showError(err);
    });
  };

  resendVerification = async () => {
    const oauth = clientController.client.oauth;
    const data = {
      authToken: oauth["access_token"]
    };
    return stateController
      .dismissPopup()
      .then(() =>
        stateController.showPopup({
          title: UIText.title,
          content: UIText.pleaseWait
        })
      )
      .then(() =>
        apiService.async("POST", {
          endpoint: endpointConfig.resend_verification,
          headers: serverConfig.defaultHeaders,
          data
        })
      )
      .then(stateController.dismissPopup)
      .then(() =>
        stateController.showPopup({
          title: UIText.title,
          content: UIText.verificationResent,
          leftButtonText: UIText.generalConfirm,
          leftButtonPress: () =>
            stateController.dismissPopup().then(clientController.execLogout)
        })
      )
      .catch(this._showError);
  };

  // MFA Methods
  getTrustCookie = () => {
    const cookieName = `${env}-mcb-trust`;
    return getCookie(cookieName) || "";
  };

  setTrustCookie = (value, days) => {
    const cookieName = `${env}-mcb-trust`;
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${d.toUTCString()}`;
    document.cookie = `${cookieName}=${value}; ${expires}; path=/; domain=.${rootDomain}; SameSite=Lax; Secure`;
  };

  verifyMfaCode = async () => {
    if (!this.mfaCode) return;
    this.mfaVerifying = true;
    this.mfaError = "";

    // Get existing trust cookie value
    const existingTrustCookieValue = this.getTrustCookie();

    return apiService
      .async("POST", {
        endpoint: "/auth/mfa/verify-session",
        headers: serverConfig.defaultHeaders,
        data: {
          sessionId: this.mfaSessionId,
          code: this.mfaCode,
          trustDevice: this.mfaTrust,
          existingTrustCookieValue,
          environment: env
        }
      })
      .then(response => {
        const data = response.data || {};

        if (!data.verified) {
          // Failed verification
          if (data.maxAttemptsExceeded) {
            this.mfaError = "Maximum attempts exceeded. Please start again.";
            this.mfaAttemptsRemaining = 0;
          } else {
            this.mfaAttemptsRemaining = data.attemptsRemaining || 0;
            this.mfaError = `Invalid code. ${
              this.mfaAttemptsRemaining
            } attempt(s) remaining.`;
          }
          this.mfaVerifying = false;
          return;
        }

        // Success - set OAuth data and trust cookie
        const oauth = data.oauth;
        const cookie = data.trustCookie;
        if (cookie && cookie.value && cookie.days) {
          this.setTrustCookie(cookie.value, cookie.days);
        }

        this.mfaRequired = false;
        this.mfaVerifying = false;

        // Continue with normal login flow
        return clientController
          .updateOAuth2Data(oauth)
          .then(() =>
            stateController.showPopup({
              title: UIText.postLoginProgress,
              content: UIText.pleaseWait
            })
          )
          .then(clientController.initializeClient)
          .then(stateController.dismissPopup);
      })
      .catch(err => {
        this.mfaError = UIText.mfaInvalidCode;
        this.mfaVerifying = false;
      });
  };

  handleStartAgain = async () => {
    // Invalidate session on backend
    if (this.mfaSessionId) {
      await apiService
        .async("POST", {
          endpoint: "/auth/mfa/invalidate-session",
          headers: serverConfig.defaultHeaders,
          data: { sessionId: this.mfaSessionId }
        })
        .catch(console.error);
    }

    // Reset to sign-in form
    this.mfaRequired = false;
    this.mfaSessionId = "";
    this.mfaCode = "";
    this.mfaError = "";
    this.mfaAttemptsRemaining = 3;
  };

  updateMfaCode = event => {
    if (!event) return;
    if (event.nativeEvent && typeof event.nativeEvent.text === "string") {
      this.mfaCode = event.nativeEvent.text;
    }
  };

  toggleMfaTrust = () => {
    this.mfaTrust = !this.mfaTrust;
  };
}
